import NumberFlow from '@number-flow/react'
import { RESET } from 'jotai/utils'
import { useCallback, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import Countdown from 'src/components/Countdown'
import { MembershipIcon } from 'src/components/Membership/MembershipIcon'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import useNewUserDiscount from 'src/features/Membership/hooks/useNewUserDiscount'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { useCountdownTimer } from 'src/utils/hooks/useCountdownTimer'
import { startEventGroup, track } from 'src/utils/tracker'
import { withQuery } from 'ufo'
import MdiChevronRight from '~icons/mdi/chevron-right'
import RiDiscountPercentFill from '~icons/ri/discount-percent-fill'
import { useMembershipPlansStateWriteOnlyAtom } from '../../Membership/context'
import {
  useMembershipDiscountValidBefore,
  useUserIsEligibleForDiscount,
} from './discount-utils'

/**
 * One-time discount
 * @deprecated
 * @param props
 * @returns
 */
export function MembershipCommonDiscountTopBanner(props: StyleProps) {
  const { t } = useTranslation(['growth'])

  return (
    <div
      className={cx('bg-gradient-discount', props.className)}
      style={props.style}
    >
      <Link
        to="/membership/plans"
        className="h-8 flex justify-center items-center hover:bg-black/10 no-underline text-white font-medium text-shadow shadow-black/50 transition"
        onClick={() => {
          const trackingLocation = 'common-discount-banner'
          track('enter_member_click', { location: trackingLocation })
          startEventGroup('member_source', trackingLocation, {
            persistent: true,
          })
        }}
      >
        <MembershipIcon className="w-5 h-5 mr-2 -translate-y-px drop-shadow" />
        <span>
          {t('growth:member-discount-event.banner.one-time.description')}
        </span>
        <MdiChevronRight className="w-6 h-6 drop-shadow" />
      </Link>
    </div>
  )
}

/**
 * Triggers on user canceled a monthly payment
 * @deprecated
 * @param props
 * @returns
 */
export function MembershipRecallDiscountTopBanner(props: StyleProps) {
  const { t } = useTranslation(['growth'])
  const [beforeDate] = useMembershipDiscountValidBefore()
  const nowTime = new Date().getTime()
  const timeLeft = useCountdownTimer((beforeDate ?? nowTime) - nowTime)

  return (
    <div
      className={cx(
        'bg-gradient-to-r from-purple-600 via-fuchsia-400 to-rose-400',
        props.className,
      )}
      style={props.style}
    >
      <Link
        to="/membership/plans"
        className="h-8 flex justify-center items-center hover:bg-black/10 no-underline text-white font-medium text-shadow shadow-black/50 transition"
        onClick={() => {
          const trackingLocation = 'recall-discount-banner'
          track('enter_member_click', { location: trackingLocation })
          startEventGroup('member_source', trackingLocation, {
            persistent: true,
          })
        }}
      >
        <MembershipIcon className="w-5 h-5 mr-2 -translate-y-px drop-shadow" />
        <span>
          {t('growth:member-discount-event.banner.payment-cancel.description', {
            time: dayjs.duration(timeLeft).format('HH:mm:ss'),
          })}
        </span>
        <MdiChevronRight className="w-6 h-6 drop-shadow" />
      </Link>
    </div>
  )
}

export function NewMemberDiscountCleanup() {
  const { data, loading } = useUserIsEligibleForDiscount()
  const userEligible = data.recallAvailable

  const [beforeDate, setBeforeDate] = useMembershipDiscountValidBefore()
  const setState = useMembershipPlansStateWriteOnlyAtom()
  useEffect(() => {
    if (!loading && !userEligible) {
      setBeforeDate(RESET)
      // unclaim on logout
      setState(prev => ({
        ...prev,
        clearMembershipDiscountValidBefore: false,
      }))
    }
  }, [beforeDate, userEligible, loading])

  return null
}

function MembershipDiscountClaimDialogContent(props: { onClose: () => void }) {
  const navigate = useNavigate()
  const { t } = useTranslation(['growth'])
  const onClick = useCallback(() => {
    track('claim_recall_discount_click', { type: 'plans-dialog' })
    props.onClose()
    navigate(
      withQuery('/membership/plans', { claimDiscount: 1, startCountdown: 1 }),
    )
  }, [])
  return (
    <div className="bg-white flex flex-col gap-4 pb-4 items-center">
      <p className="w-full py-4 bg-gradient-featured flex justify-center text-white">
        {t('growth:member-discount-event.claim-dialog.subtitle')}
      </p>
      <h1 className="font-bold text-black text-xl px-8">
        <Trans
          t={t}
          i18nKey="growth:member-discount-event.claim-dialog.title"
          components={{
            discount: (
              <span className="bg-gradient-featured bg-clip-text text-transparent text-3xl"></span>
            ),
          }}
          values={{ amount: 20 }}
        />
      </h1>
      <p className="text-neutral-600 px-8 whitespace-pre-wrap text-center text-sm">
        {t('growth:member-discount-event.claim-dialog.description', {
          amount: 20,
        })}
      </p>
      <button
        onClick={onClick}
        className="py-3 px-6 bg-theme-primary text-white rounded-full text-sm"
      >
        {t('growth:member-discount-event.claim-dialog.action-btn.label')}
      </button>
    </div>
  )
}

export function openMembershipDiscountClaimDialog(args?: {
  onClose?: () => void
}) {
  return UIDialog.create({
    dismissable: true,
    className: 'overflow-hidden',
    onClose: () => args?.onClose?.(),
    children: ({ close }) => (
      <MembershipDiscountClaimDialogContent onClose={() => close()} />
    ),
  })
}

export function CreditPackDiscountCountdownBannerForNewUser(props: StyleProps) {
  const endTime = useNewUserDiscount()

  if (!endTime) return null

  return (
    <section
      className={cx(
        'border border-[--event-yellow]/40 bg-gradient-to-br from-[--event-yellow]/15 via-[--event-yellow]/8 to-transparent rounded-xl',
        'py-3 px-4 mx-2 sm:mx-0 sm:py-4 sm:px-6',
        'flex items-center justify-between gap-2 sm:gap-4',
        props.className,
      )}
    >
      {/* Title, Icon, and Description */}
      <div className="flex flex-col items-start gap-1 flex-1 min-w-0 pr-2 sm:pr-4">
        <div className="flex items-center gap-2 md:gap-3 min-w-0 w-full">
          <RiDiscountPercentFill className="size-6 sm:size-7 md:size-9 text-[--event-yellow] flex-shrink-0" />
          <h2 className="font-bold text-lg sm:text-xl md:text-2xl leading-tight min-w-0">
            <Text i18nKey="growth:credit-pack-discount-event.new-member-discount.title" />
          </h2>
        </div>

        {/* Description */}
        <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed w-full break-words">
          <Text i18nKey="growth:credit-pack-discount-event.new-member-discount.side-note" />
        </p>
      </div>

      {/* Countdown */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="font-medium text-xs sm:text-sm md:text-sm text-dspurple-400 dark:text-purple-500 leading-tight text-right">
          <Text i18nKey="growth:credit-pack-discount-event.new-member-discount.countdown-text" />
        </span>

        <Countdown targetDate={endTime} utc>
          {({ timeLeft }) => (
            <div className="inline-flex items-center gap-0.5 sm:gap-1 tabular-nums">
              {dayjs
                .duration(timeLeft)
                .format('H:m:s')
                .split(':')
                .map(Number)
                .map((num, idx) => (
                  <div key={idx} className="inline-flex items-center">
                    {idx > 0 && (
                      <span className="text-dspurple-400 dark:text-purple-500 px-0.5 sm:px-1 text-sm font-medium">
                        :
                      </span>
                    )}
                    <div className="bg-dspurple-400 dark:bg-dspurple-500 rounded px-1.5 py-1 sm:px-2 sm:py-1.5 min-w-6 sm:min-w-8 text-center shadow-sm">
                      <NumberFlow
                        className="text-white text-xs sm:text-sm md:text-base font-bold"
                        value={num}
                        trend={-1}
                        digits={idx === 0 ? undefined : { 1: { max: 5 } }}
                        format={
                          idx === 0 ? undefined : { minimumIntegerDigits: 2 }
                        }
                      />
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Countdown>
      </div>
    </section>
  )
}
